import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { flushSync } from "react-dom";
import classNames from "classnames";
import { useApolloClient, useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import { useNavigate, Path } from "@/router";
import AppLayout from "@/layouts/AppLayout";
import PageHeading from "@/components/PageHeading";
import SearchInput from "@/components/SearchInput";
import GlobalSearchRow from "@/components/GlobalSearch/GlobalSearchRow";
import { GLOBAL_SEARCH_ROW_CLASS, GLOBAL_SEARCH_SELECTED_ROW_CLASS } from "@/components/GlobalSearch/GlobalSearch";
import { highlightMatchedChars, truncatedSupportingText } from "@/components/GlobalSearch/entityListItemContent";
import { matchDisplayNameIndices } from "@/lib/globalSearchMatch";
import EmptyState from "@/components/EmptyState";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import Avatar from "@/components/Avatar";
import { TableHeading } from "@/components/Table";
import usePageTitle from "@/hooks/usePageTitle";
import useCurrentUser from "@/hooks/useCurrentUser";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { SOME_USERS_QUERY, type BackofficeUserOption } from "@/pages/people/queries";
import { useAssumeUser } from "@/providers/AssumeUserProvider";
import { getInternalReturnUrl, readAssumedUser } from "@/providers/AssumeUserProvider/storage";
import { CAPABILITIES_QUERY } from "@/providers/AuthorizationProvider/queries";
import { getImpersonationFailureFromError, type ImpersonationFailure } from "@/providers/GraphqlProvider/errors";
import { getImpersonationHeaders } from "@/providers/GraphqlProvider/headers";

const PREFLIGHT_FALLBACK_ERROR: ImpersonationFailure = {
  code: "IMPERSONATION_FAILED",
  reason: "UNKNOWN",
  message: "We couldn't verify this user's Member 360 access. Try again later.",
  retryable: true
};
const PREFLIGHT_TARGET_MISMATCH_ERROR: ImpersonationFailure = {
  code: "IMPERSONATION_FAILED",
  reason: "TARGET_NOT_CONFIRMED",
  message: "We couldn't verify that Member 360 switched to the selected user. Try again.",
  retryable: true
};
const RESET_STORE_ERROR: ImpersonationFailure = {
  code: "IMPERSONATION_FAILED",
  reason: "IMPERSONATION_SETUP_FAILED",
  message: "We couldn't clear cached Member 360 data before switching users. Try again.",
  retryable: true
};
const MISSING_EMAIL_ERROR: ImpersonationFailure = {
  code: "IMPERSONATION_FAILED",
  reason: "TARGET_EMAIL_MISSING",
  message: "The selected user has no email address, so they can't be assumed. Contact Technology if this looks wrong.",
  retryable: false
};

const REASON_LABELS: Record<string, string> = {
  ACTOR_NOT_PERMITTED: "You are not permitted to assume users",
  FEATURE_DISABLED: "Assumed-user access is disabled",
  FEATURE_FLAG_UNAVAILABLE: "Assumed-user access is temporarily unavailable",
  INVALID_HEADER: "The assumed-user request was invalid",
  TARGET_NOT_FOUND: "The selected user could not be found",
  TARGET_NOT_CONFIRMED: "The selected user could not be confirmed",
  IMPERSONATION_SETUP_FAILED: "Assumed-user setup failed",
  UNSUPPORTED_PROVIDER: "The selected user is not linked to a supported identity provider",
  INVALID_PROVIDER_IDENTIFIER: "The selected user's identity provider record is incomplete",
  MISSING_AUTHORIZATION_PROFILE: "The selected user's authorization profile is missing",
  TARGET_EMAIL_MISSING: "The selected user has no email address",
  MALFORMED_AUTHORIZATION_PROFILE: "The selected user's authorization profile is incomplete",
  NO_M360_ROLES: "The selected user has no active Member 360 backoffice role",
  NO_M360_CAPABILITIES: "The selected user has no active Member 360 capabilities",
  IDENTITY_PROVIDER_UNAVAILABLE: "The identity provider could not be reached"
};

function getReasonLabel(reason: string): string {
  return REASON_LABELS[reason] ?? reason.replace(/_/g, " ").toLowerCase();
}

// `backofficeUsers.email` is nullable on the backend. This page handles
// null emails (no highlight crash, explicit error instead of a silent
// assume), so widen at the boundary instead of changing the shared option
// type and its ten other consumers.
type AssumeUserOption = Omit<BackofficeUserOption, "email"> & { email: string | null };

function emailSubstringIndices(email: string | null | undefined, query: string): number[] {
  if (!email) return [];
  const haystack = email.toLowerCase();
  const indices: number[] = [];
  for (const token of query.toLowerCase().split(/\s+/).filter(Boolean)) {
    let at = haystack.indexOf(token);
    while (at >= 0) {
      for (let i = at; i < at + token.length; i += 1) indices.push(i);
      at = haystack.indexOf(token, at + 1);
    }
  }
  return indices;
}

function assumeUserSupportingText(result: AssumeUserOption, query: string) {
  const email = result.email ?? "";
  const supporting = [email, result.department].filter(Boolean).join(" · ");
  if (!query.includes("@")) return truncatedSupportingText(supporting);
  const emailIndices = emailSubstringIndices(email, query);
  if (emailIndices.length === 0) return truncatedSupportingText(supporting);
  return (
    <span className="block min-w-0 truncate text-left">
      {highlightMatchedChars(email, emailIndices)}
      {result.department ? ` · ${result.department}` : null}
    </span>
  );
}

function AssumeUserNotes() {
  return (
    <section
      className="mt-8 rounded-2xl border border-sys-brand-outline bg-sys-brand-surface p-6 shadow-web-1 sm:p-8"
      data-testid="assume-user-notes"
    >
      <TableHeading heading="Additional Information" topSpacing="none" />
      <ul className="list-disc space-y-2 pl-5 text-body-medium">
        <li>
          <strong>You are able to execute actions on behalf of the assumed user</strong>, but we will track that
          it&apos;s you in the backend.
        </li>
        <li>
          Recently Visited shows the assumed user&apos;s history, not yours. Leftover recents in this browser are not
          imported while you are assuming someone.
        </li>
        <li>
          Inactive users who are still in our system cannot be assumed. You&apos;ll get an error message when you try.
        </li>
        <li>Amplitude analytics will track the authenticated user, not the assumed user.</li>
        <li>
          This feature is <strong>NOT</strong> available for the M360 portal site, yet.
        </li>
      </ul>
    </section>
  );
}

export default function AssumeUserPage() {
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.assumeUser });
  usePageTitle("/assume-user", "Assume a User");

  const navigate = useNavigate();
  const client = useApolloClient();
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const { assumedUser, startAssumingUser, confirmAssumedUserAuthorization, stopAssumingUser, clearAssumedUserSession } =
    useAssumeUser();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [preflightError, setPreflightError] = useState<ImpersonationFailure | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [lastTarget, setLastTarget] = useState<AssumeUserOption | null>(null);
  const returnUrl = getInternalReturnUrl(searchParams.get("return_to"));
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const highlightedRef = useRef<HTMLDivElement | null>(null);
  const isStartingRef = useRef(false);

  const {
    data,
    loading,
    error: searchError,
    refetch
  } = useQuery(SOME_USERS_QUERY, {
    variables: { search: debouncedSearchTerm, forImpersonation: true },
    skip: debouncedSearchTerm.length === 0 || !!assumedUser
  });

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const users = (data?.backofficeUsers ?? []) as AssumeUserOption[];
  const hasQuery = debouncedSearchTerm.length > 0;
  // Apollo keeps previous data while loading or after an error; never show or
  // act on those stale rows.
  const visibleUsers = loading || searchError ? [] : users;
  const hasResults = visibleUsers.length > 0;

  useEffect(() => {
    setHighlightedIndex(0);
  }, [debouncedSearchTerm, visibleUsers.length]);

  const clampedHighlight = Math.min(highlightedIndex, Math.max(visibleUsers.length - 1, 0));

  useEffect(() => {
    highlightedRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [clampedHighlight, debouncedSearchTerm]);

  const handleAssumeUser = async (targetUser: AssumeUserOption) => {
    if (isStartingRef.current) return;
    isStartingRef.current = true;

    // The assumed-user session requires a non-empty email; fail loudly
    // instead of persisting a session readAssumedUser would reject.
    const targetEmail = targetUser.email;
    if (!targetEmail) {
      setLastTarget(null);
      setPreflightError(MISSING_EMAIL_ERROR);
      isStartingRef.current = false;
      return;
    }

    setIsStarting(true);
    setLastTarget(targetUser);
    setPreflightError(null);

    try {
      const { data: preflightData } = await client.query({
        query: CAPABILITIES_QUERY,
        context: { headers: getImpersonationHeaders(targetUser.id) },
        fetchPolicy: "no-cache"
      });

      if (preflightData?.me?.id !== targetUser.id) {
        setPreflightError(PREFLIGHT_TARGET_MISMATCH_ERROR);
        return;
      }

      flushSync(() => {
        startAssumingUser({
          id: targetUser.id,
          name: targetUser.name,
          email: targetEmail,
          photo: targetUser.photo,
          department: targetUser.department,
          returnUrl
        });
      });

      try {
        await client.resetStore();
      } catch {
        clearAssumedUserSession(targetUser.id);
        setPreflightError(RESET_STORE_ERROR);
        return;
      }

      const activeAssumedUser = readAssumedUser(user.sub);
      if (activeAssumedUser?.id !== targetUser.id) {
        return;
      }

      confirmAssumedUserAuthorization();
      navigate(returnUrl as Path);
    } catch (error) {
      clearAssumedUserSession(targetUser.id);
      setPreflightError(getImpersonationFailureFromError(error) ?? PREFLIGHT_FALLBACK_ERROR);
    } finally {
      isStartingRef.current = false;
      setIsStarting(false);
    }
  };

  const handleStopAssumingUser = () => {
    const activeReturnUrl = getInternalReturnUrl(assumedUser?.returnUrl);
    stopAssumingUser();
    navigate(activeReturnUrl as Path);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // During IME composition Enter confirms the composition, not the selection.
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    // The list still shows the previous query's rows during the 300ms
    // debounce window; keyboard acts on the highlight, which may sit on a
    // stale row, so ignore it until the debounced query catches up. Clicks
    // are not gated: they select an explicitly chosen visible row.
    if (isDebouncing) return;
    if (event.key === "ArrowDown" && hasResults) {
      event.preventDefault();
      setHighlightedIndex((index) => Math.min(index + 1, visibleUsers.length - 1));
      return;
    }
    if (event.key === "ArrowUp" && hasResults) {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === "Enter" && hasResults) {
      event.preventDefault();
      void handleAssumeUser(visibleUsers[Math.min(highlightedIndex, visibleUsers.length - 1)]);
    }
  };

  const isDebouncing = searchTerm.trim() !== debouncedSearchTerm;

  return (
    <AppLayout interior>
      <div className="mx-auto max-w-3xl pb-12">
        <PageHeading
          title="Assume a User"
          containerClassName="pt-6 text-center lg:pt-8"
          titleClassName="text-sys-on-surface text-headline-medium-bold font-serif lg:text-headline-large-bold"
        />
        {assumedUser && !isStarting ? (
          <div className="pt-8">
            <Banner
              variant="info"
              description={`You are already viewing Member 360 as ${assumedUser.name}.`}
              subText="Stop assuming this user before starting another session."
              primaryAction={{ label: "Stop assuming", onClick: handleStopAssumingUser }}
              hideSecondaryDismiss
              testId="assume-user-already-active"
            />
          </div>
        ) : (
          <div className="mt-8 overflow-hidden rounded-2xl border border-sys-brand-outline bg-sys-brand-surface shadow-web-1">
            <div className="flex items-center gap-4 border-b border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest px-6 py-5 sm:px-8">
              <Avatar className="shrink-0" iconClassName="text-sys-brand-on-secondary-container" />
              <p className="text-body-medium text-sys-brand-on-surface-variant">
                Search for a user to view Member 360 with their access and permissions. Selecting a user starts assuming
                them.
              </p>
            </div>
            <div className="space-y-6 p-6 sm:p-8">
              <SearchInput
                ref={inputRef}
                query={searchTerm}
                onChange={(value) => {
                  setPreflightError(null);
                  setSearchTerm(value);
                }}
                onClear={() => {
                  setPreflightError(null);
                  setDebouncedSearchTerm("");
                }}
                placeholder="Search by name, or email with @"
                ariaLabel="Search users to assume"
                pageSearchShortcut={false}
                onKeyDown={handleSearchKeyDown}
                listboxId={listboxId}
                listboxExpanded={hasResults && !isStarting}
                activeOptionId={hasResults ? `${listboxId}-option-${clampedHighlight}` : undefined}
                disabled={isStarting}
                type="search"
              />

              {searchError && !loading ? (
                <Banner
                  variant="error"
                  description="We couldn’t search for users in Member 360."
                  subText="Try again or contact Technology if the problem continues."
                  primaryAction={{ label: "Try again", onClick: () => void refetch() }}
                  testId="assume-user-search-error"
                />
              ) : null}

              {preflightError ? (
                <Banner
                  variant="error"
                  description={preflightError.message}
                  subText={`Reason: ${getReasonLabel(preflightError.reason)}.`}
                  primaryAction={
                    lastTarget && preflightError.retryable
                      ? { label: "Try again", onClick: () => void handleAssumeUser(lastTarget) }
                      : undefined
                  }
                  testId="assume-user-preflight-error"
                />
              ) : null}

              {isStarting ? (
                <div
                  role="status"
                  className="py-3 text-center text-sys-brand-on-surface-variant"
                  data-testid="assume-user-verifying"
                >
                  Verifying user...
                </div>
              ) : loading && hasQuery ? (
                <div role="status" className="py-3 text-center text-sys-brand-on-surface-variant">
                  Searching...
                </div>
              ) : searchError ? null : hasResults ? (
                <div
                  id={listboxId}
                  role="listbox"
                  aria-label="Matching users"
                  className="flex max-h-[300px] flex-col gap-1 overflow-y-auto"
                  data-testid="assume-user-results"
                >
                  {visibleUsers.map((result, index) => {
                    const selected = index === clampedHighlight;
                    return (
                      <div
                        key={result.id}
                        id={`${listboxId}-option-${index}`}
                        ref={selected ? highlightedRef : undefined}
                        role="option"
                        aria-selected={selected}
                        aria-label={[result.name, result.email, result.department].filter(Boolean).join(", ")}
                        data-testid={`assume-user-result-${index}`}
                        className={classNames(GLOBAL_SEARCH_ROW_CLASS, selected && GLOBAL_SEARCH_SELECTED_ROW_CLASS)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        onClick={() => void handleAssumeUser(result)}
                      >
                        <GlobalSearchRow
                          iconName="person"
                          picture={result.photo ?? undefined}
                          headline={highlightMatchedChars(
                            result.name,
                            matchDisplayNameIndices(result.name, debouncedSearchTerm)
                          )}
                          supportingText={assumeUserSupportingText(result, debouncedSearchTerm)}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : hasQuery ? (
                <div role="status">
                  <EmptyState title="No matching users" caption="Try a different search" />
                </div>
              ) : (
                <div role="status">
                  <EmptyState title="Search for a user to assume" caption="Results appear here as you type" />
                </div>
              )}

              <div className="flex flex-wrap justify-end gap-3 pt-6">
                <Button variant="text" type="button" disabled={isStarting} onClick={() => navigate(returnUrl as Path)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
        <AssumeUserNotes />
      </div>
    </AppLayout>
  );
}
